import { useEffect, useState } from 'react';
import { getPlanReturns } from '../api/plan-returns/getPlanReturns';
import { PlanReturnView, toArcaPlanReturnsBySlug } from '../utils/transferReturns';
import { validYearsToRetirement } from '../utils/monthlyProjection';
import { ArcaPlanReturnsBySlug } from './useArcaPlanReturns';

/**
 * Una petición por sesión, plan y años hasta la jubilación. Solo se guardan las respuestas
 * buenas; un fallo se vuelve a pedir la próxima vez (como `useArcaPlanReturns`).
 */
const cache = new Map<string, Promise<PlanReturnView | null | 'error'>>();

/** Solo para tests: vacía la caché entre casos. */
export function resetOwnPlanReturnCache(): void {
	cache.clear();
}

function load(
	token: string,
	slug: string,
	yearsToRetirement: number | undefined
): Promise<PlanReturnView | null | 'error'> {
	const key = `${token}|${slug}|${yearsToRetirement ?? ''}`;
	if (!cache.has(key)) {
		const promise = getPlanReturns(token, { arcaPlans: [slug], yearsToRetirement })
			.then((res) => (res ? toArcaPlanReturnsBySlug(res)[slug] ?? null : ('error' as const)))
			// Respuesta mal formada: como un fallo.
			.catch(() => 'error' as const)
			.then((view) => {
				if (view === 'error') cache.delete(key);
				return view;
			});
		cache.set(key, promise);
	}
	return cache.get(key) as Promise<PlanReturnView | null | 'error'>;
}

/**
 * Rentabilidad del plan del PROPIO usuario (su slug del catálogo), decidida por comparsa.
 *
 * `/plan-returns/arca-plans` solo trae los planes que ofrecemos (activos). Un plan que alguien
 * tiene pero ya no se ofrece, como «Indexa Más Rentabilidad, PPI», no viene en esa lista: se
 * pide aparte a `/plan-returns?arcaPlans=<slug>`, que sí lo calcula (comparsa#826,
 * chirigota#598). Si el plan viene en la lista, se usa esa entrada y no se pide nada más.
 *
 * `slug`: `undefined` si aún no se sabe cuál es su plan (`/users/plans` sin llegar), `null` si no
 * tiene. Devuelve `undefined` mientras llega algo («cargando»); `null` sin plan, sin datos o si
 * falla (nunca una tasa de respaldo).
 */
export default function useOwnPlanReturn(
	token: string,
	slug: string | null | undefined,
	yearsToRetirementRaw: number | undefined,
	arcaPlanReturns: ArcaPlanReturnsBySlug | undefined
): PlanReturnView | null | undefined {
	const yearsToRetirement = validYearsToRetirement(yearsToRetirementRaw);
	const inList = !!slug && !!arcaPlanReturns && slug in arcaPlanReturns;
	const needsOwnRequest = !!token && !!slug && !!arcaPlanReturns && !inList;
	// Con la clave de lo pedido: al cambiar de plan no se enseña ni un render la del anterior.
	const ownKey = `${token}|${slug}|${yearsToRetirement ?? ''}`;
	const [own, setOwn] = useState<{ key: string; view: PlanReturnView | null } | null>(null);

	useEffect(() => {
		if (!needsOwnRequest || !slug) return;
		let cancelled = false;
		load(token, slug, yearsToRetirement).then((view) => {
			if (!cancelled) setOwn({ key: ownKey, view: view === 'error' ? null : view });
		});
		return () => {
			cancelled = true;
		};
	}, [needsOwnRequest, ownKey, token, slug, yearsToRetirement]);

	if (arcaPlanReturns === undefined || !token || slug === undefined) return undefined;
	if (!slug) return null;
	if (inList) return arcaPlanReturns[slug] ?? null;
	return own?.key === ownKey ? own.view : undefined;
}
